import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Loader } from 'lucide-react';
import { AuthenticatedImage } from './authenticated-image';

const errorFallback = (
  <div className='w-full h-full flex items-center justify-center bg-gray-100'>
    <svg
      className='w-8 h-8 text-gray-400'
      fill='none'
      stroke='currentColor'
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
      />
    </svg>
  </div>
);

type ImageCarouselProps = {
  photos: string[];
  placeName: string;
};

const ImageCarouselComponent = ({ photos, placeName }: ImageCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const [loadedIndexes, setLoadedIndexes] = useState<Set<number>>(new Set([0]));
  const [readyIndexes, setReadyIndexes] = useState<Set<number>>(new Set());
  const photosCount = photos?.length ?? 0;

  // Preload adjacent images (next and previous)
  React.useEffect(() => {
    if (photosCount > 1) {
      const nextIndex = (currentIndex + 1) % photosCount;
      const prevIndex = (currentIndex - 1 + photosCount) % photosCount;

      setLoadedIndexes((loaded) => {
        const updated = new Set(loaded);
        updated.add(currentIndex);
        updated.add(nextIndex);
        updated.add(prevIndex);
        return updated;
      });
    }
  }, [currentIndex, photosCount]);

  // Handle pending transitions - wait for image to be ready
  React.useEffect(() => {
    if (pendingIndex !== null && readyIndexes.has(pendingIndex)) {
      setCurrentIndex(pendingIndex);
      setPendingIndex(null);
    }
  }, [pendingIndex, readyIndexes]);

  // Reset state when photos change
  React.useEffect(() => {
    setCurrentIndex(0);
    setPendingIndex(null);
    setLoadedIndexes(new Set([0]));
    setReadyIndexes(new Set());
  }, [photos]);

  // Navigate to a specific index
  const navigateToIndex = React.useCallback((targetIndex: number) => {
    setReadyIndexes((ready) => {
      if (ready.has(targetIndex)) {
        // Image is ready, navigate immediately
        setCurrentIndex(targetIndex);
        setPendingIndex(null);
      } else {
        // Image not ready, set as pending and ensure it's loading
        setPendingIndex(targetIndex);
        setLoadedIndexes((loaded) => {
          const updated = new Set(loaded);
          updated.add(targetIndex);
          return updated;
        });
      }
      return ready;
    });
  }, []);

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    const nextIdx = (currentIndex + 1) % photosCount;
    navigateToIndex(nextIdx);
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    const prevIdx = (currentIndex - 1 + photosCount) % photosCount;
    navigateToIndex(prevIdx);
  };

  if (photosCount === 0) {
    return null;
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <div className='relative w-full h-full overflow-hidden bg-gray-100 shadow-sm group'>
        {/* Render all loaded images stacked with opacity transitions */}
        {photos.map((photo, idx) => {
          const isActive = idx === currentIndex;
          const shouldRender = loadedIndexes.has(idx);

          if (!shouldRender) {
            return null;
          }

          return (
            <div
              key={idx}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: isActive ? 1 : 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: isActive ? 'auto' : 'none'
              }}
            >
              <AuthenticatedImage
                src={photo}
                alt={`${placeName} ${idx + 1} of ${photos.length}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
                onLoad={() => {
                  setReadyIndexes((ready) => {
                    const updated = new Set(ready);
                    updated.add(idx);
                    return updated;
                  });
                }}
                fallback={errorFallback}
              />
            </div>
          );
        })}

        {/* Loading spinner (shown when waiting for pending image) */}
        {pendingIndex !== null && (
          <div
            className='absolute inset-0 flex items-center justify-center bg-black/30 z-10'
            style={{ pointerEvents: 'none' }}
          >
            <Loader className='w-8 h-8 text-white animate-spin' />
          </div>
        )}

        {photos.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className='absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full shadow-lg transition-all z-20'
              title='Previous image'
            >
              <ChevronLeft className='w-5 h-5' />
            </button>
            <button
              onClick={nextImage}
              className='absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full shadow-lg transition-all z-20'
              title='Next image'
            >
              <ChevronRight className='w-5 h-5' />
            </button>
            <div className='absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 px-3 py-1.5 rounded-full z-20'>
              <span className='text-white text-sm font-medium'>
                {currentIndex + 1} / {photos.length}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Memoize with custom comparison to check if photos array content is the same
export const ImageCarousel = React.memo(
  ImageCarouselComponent,
  (prevProps, nextProps) => {
    // Same place name and same photos
    if (prevProps.placeName !== nextProps.placeName) return false;
    if (prevProps.photos === nextProps.photos) return true;
    if (prevProps.photos.length !== nextProps.photos.length) return false;

    // Check if all photo URLs are the same
    for (let i = 0; i < prevProps.photos.length; i++) {
      if (prevProps.photos[i] !== nextProps.photos[i]) return false;
    }
    return true;
  }
);
